<template>
  <div class="relative" v-if="!isCustom">
    <label v-if="label" class="block text-sm mb-1">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>
    <button
      @click="isOpen = !isOpen"
      type="button"
      :class="isOpen ? 'outline-primary-blue' : 'outline-transparent'"
      class="outline-2 bg-gray-100/75 rounded-lg text-[15px] w-full flex items-center justify-between px-3.5 h-12 cursor-pointer"
    >
      <slot name="select-title"></slot>
      <BaseLoader size="size-5 min-w-5" v-if="loading" />
      <div v-else class="flex items-center justify-center text-gray-600">
        <Icon name="tabler:chevron-up" v-if="isOpen" size="20" />
        <Icon name="tabler:chevron-down" size="20" v-else />
      </div>
    </button>
    <div
      v-if="isOpen"
      class="fixed inset-0 z-99 flex items-center justify-center bg-black/25"
    ></div>
    <Transition
      enter-active-class="slideInUp"
      leave-active-class="slideOutDown"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-100 flex sm:items-center items-end justify-center bg-transparent"
      >
        <div
          @click.stop
          class="sm:w-96 w-full bg-white border border-gray-200 shadow-2xl rounded-t-lg sm:rounded-b-lg flex flex-col overflow-hidden"
          :class="[height]"
        >
          <div
            class="px-4 py-3 border-b border-gray-100 flex items-center justify-between"
          >
            <span></span>
            <span class="font-semibold capitalize">{{ title }}</span>
            <button
              @click.stop="isOpen = false"
              class="size-6 min-w-6 hover:bg-gray-200 cursor-pointer rounded-full transition-colors flex items-center justify-center"
            >
              <Icon name="tabler:x" size="16" />
            </button>
          </div>
          <div class="w-full bg-white">
            <input
              v-model="search"
              class="h-10 border-t text-sm placeholder:text-[13px] border-b border-gray-200 focus:border-gray-300 outline-none rounded-none px-3.5 w-full bg-white"
              type="search"
              :placeholder="placeholder"
            />
          </div>
          <div class="overflow-y-auto p-2" v-if="items.length">
            <div
              class=""
              v-bind="containerProps"
              :style="{ height: `${height}px` }"
              ref="virtualListContainer"
            >
              <div class="" v-bind="wrapperProps">
                <div
                  @click="openCustom"
                  v-if="customEnambled"
                  class="p-2.5 bg-primary-blue/5 cursor-pointer hover:bg-gray-100 rounded flex items-center justify-between"
                >
                  <span>{{ $t("new.manual_input") }}</span>
                  <Icon name="ph:plus-circle" size="18" />
                </div>
                <div
                  @click="selectItem(item)"
                  class="p-2.5 cursor-pointer hover:bg-gray-100 rounded text-[15px]"
                  v-for="(item, index) in items"
                  :key="item.id || index"
                >
                  <slot name="item" :item="item"></slot>
                </div>
              </div>
            </div>
          </div>
          <div class="flex flex-col items-center space-y-2 py-12 px-5" v-else>
            <div class="flex items-center justify-center space-x-2">
              <Icon name="ph:magnifying-glass" size="16" />
              <p class="text-center text-sm text-gray-500">
                {{ emptyListTitle }}
              </p>
            </div>
            <BaseButton
              rounded="rounded"
              class="w-full"
              v-if="customEnambled"
              @click="openCustom"
              :label="$t('new.manual_input')"
            >
              <template #icon>
                <Icon name="ph:pencil-simple" size="18" />
              </template>
            </BaseButton>
          </div>
        </div>
      </div>
    </Transition>
  </div>
  <div class="" v-else>
    <label class="block text-sm mb-1">{{ $t("new.manual_input") }}</label>
    <input
      :placeholder="customPlaceholder"
      class="block w-full px-3.5 h-12 bg-gray-100/75 rounded-lg outline-2 focus:outline-primary-blue outline-transparent text-[15px] placeholder:text-sm"
      v-model="customValue"
      :label="customLabel"
    />
    <div class="mt-1 w-full">
      <button
        type="button"
        class="w-full flex justify-start text-left text-primary-blue text-sm cursor-pointer"
        @click.stop="closeCustom"
      >
        {{ $t("new.select_from_list") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useVirtualList } from "@vueuse/core";

const emit = defineEmits(["select", "custom-mode"]);

const virtualListContainer = ref<HTMLElement | null>(null);
const search = defineModel("search", { default: "" });
const customValue = defineModel("customValue", { default: "" });

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    items?: any[];
    title?: string;
    position?: "bottom" | "top";
    height?: number;
    emptyListTitle: string;
    required?: boolean;
    label?: string;
    placeholder?: string;
    customEnambled?: boolean;
    customLabel?: string;
    customPlaceholder?: string;
  }>(),
  {
    loading: false,
    title: "Select",
    items: () => ["Item 1 ", "Item 2", "Item 3"],
    position: "bottom",
    height: 200,
    emptyListTitle: "No items found by this search",
    required: true,
    label: "Select",
    placeholder: "Serch item",
    customEnambled: false,
    customLabel: "Enter custom value",
    customPlaceholder: "Enter custom value",
  },
);

const { containerProps, wrapperProps } = useVirtualList(props.items, {
  itemHeight: 40,
});
const isOpen = ref<boolean>(false);
const isCustom = ref<boolean>(false);

watch(search, () => {
  if (virtualListContainer.value && isOpen.value) {
    virtualListContainer.value.scrollTop = 0;
  }
});

const selectItem = (item: any) => {
  emit("select", item);
  isOpen.value = false;
};

// BaseSearchSelect.vue
const openCustom = () => {
  isCustom.value = true;
  emit("custom-mode", true); // emit event
};

const closeCustom = () => {
  isCustom.value = false;
  emit("custom-mode", false); // emit event
};
</script>
